// What the workshop home shows (Stage 3a plan §D1): the filter chips, the rows of the list, the
// empty texts, the search text, and the scanner's id check and outcomes. Plain
// module with no Vue, so it runs under `node --test`. Labels are i18n keys with their params; the
// chips reuse workshopView.mjs.
import { deadlineChip, listCell } from "./workshopView.mjs";

// The API's state sets, in chip order. "Aperte" is the default.
export const WORKSHOP_FILTERS = [
  { state: "open", key: "workshop.home.filters.open", color: "primary" },
  {
    state: "to_approve",
    key: "workshop.home.filters.to_approve",
    color: "amber",
  },
  {
    state: "pending_exceptions",
    key: "workshop.home.filters.pending_exceptions",
    color: "amber",
  },
  { state: "overdue", key: "workshop.home.filters.overdue", color: "red" },
  { state: "approved", key: "workshop.home.filters.approved", color: "green" },
];
export const DEFAULT_FILTER = "open";
export const PAGE_LIMIT = 50;
// Same as the API (workshop/list.js QUERY_MAX).
export const QUERY_MAX = 40;

// [{ state, key, color, count }] of the chip row; a missing count is 0.
export function filterChips(counts) {
  return WORKSHOP_FILTERS.map((filter) => ({
    ...filter,
    count: (counts && Number(counts[filter.state])) || 0,
  }));
}

// The label key of a filter, for the footer "Aperte: 5 · ordinate per scadenza".
export function filterKey(state) {
  const filter = WORKSHOP_FILTERS.find((item) => item.state === state);
  return filter ? filter.key : WORKSHOP_FILTERS[0].key;
}

// The search text sent as q: no spaces (plates and VINs have none), at most QUERY_MAX characters.
export function cleanQuery(text) {
  return String(text || "")
    .replace(/\s+/g, "")
    .slice(0, QUERY_MAX);
}

// The empty text of a list: "Nessun veicolo trovato" when a search matched nothing at all,
// otherwise the text of the selected filter.
export function emptyKey(state, query, counts) {
  const total = filterChips(counts).reduce((sum, chip) => sum + chip.count, 0);
  if (query && total === 0) return "workshop.home.empty.no_match";
  return `workshop.home.empty.${filterKey(state).split(".").pop()}`;
}

// One list item -> what a card or a table row shows:
// { checklistId, targa, vin, vehicle, chip, deadline, done, total, percent, pendingExceptions }.
// chip is the "Bonifica" chip of the vehicle lists ("In ritardo x/y" in red when unapproved and
// past due); deadline is the deadline chip of the acceptance card (null without a due date).
export function rowView(item) {
  const car = (item && item.car) || {};
  const deadline = (item && item.deadline) || {};
  const done = (item && item.done) || 0;
  const total = (item && item.total) || 0;
  const { chip } = listCell({
    status: "confirmed",
    deadlines: { depollution: { dueDate: deadline.dueDate || null } },
    workshop: {
      state: item && item.state,
      done,
      total,
      pendingExceptions: (item && item.pendingExceptions) || 0,
      deadlineStatus: deadline.status,
    },
  });
  return {
    checklistId: item && item.checklistId,
    targa: car.targa || "",
    vin: car.vin || "",
    vehicle: [car.brand, car.model].filter(Boolean).join(" "),
    chip,
    deadline: deadlineChip(deadline),
    done,
    total,
    percent: total ? Math.round((done * 100) / total) : 0,
    pendingExceptions: (item && item.pendingExceptions) || 0,
  };
}

// Number of pages of a list.
export function pageCount(count, limit = PAGE_LIMIT) {
  return Math.max(1, Math.ceil((Number(count) || 0) / limit));
}

// Whether a scanned value can be a car id; checked before asking the API.
export function isCarId(value) {
  return /^[0-9a-f]{24}$/i.test(String(value || "").trim());
}

const SCAN_CODES = [
  "car_not_found",
  "no_dossier",
  "dossier_not_confirmed",
  "no_checklist",
];

// What a failed by-car lookup shows: { key, dossierId }. dossierId is set only for
// dossier_not_confirmed (the "Apri accettazione" link); anything unexpected is "error".
export function scanOutcome(err) {
  const data = err && err.response && err.response.data;
  const code = data && data.code;
  if (
    err &&
    err.response &&
    err.response.status === 404 &&
    SCAN_CODES.includes(code)
  ) {
    const dossierId =
      code === "dossier_not_confirmed" && data.details && data.details.dossierId
        ? data.details.dossierId
        : null;
    return { key: `workshop.scanner.${code}`, dossierId };
  }
  return { key: "workshop.scanner.error", dossierId: null };
}
